import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RocheUIHeroTeaser, RocheUIImageRatio } from '@repo/roche-ui';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { parseAssetSource } from '../../utils/asset';
import { PageItem } from '../../services/teaser/types';
import { TeaserService } from '../../services/teaser';
import { blokToComponent } from '../../helpers/blok-to-component';
import { StringService } from '../../services/string';
import { BaseBlok, BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { useTranslation } from '../../hooks/use-translation';

type RocheHeroTeaserInternalPageLink = {
  story: {
    name: string;
    id: number;
    url: string;
    full_slug: string;
    preRenderContent?: PageItem;
  };
};

// remove at some point as it is unnecessary
// (use empty overline as indicator for not showing overline)
export type RocheHeroTeaserLayout = 'teaser' | 'teaser-without-overline';

export type RocheHeroTeaserImage = {
  brightcove_id?: string;
  src?: { filename: string };
  ratio?: string;
}[];

export type RocheHeroTeaserType = {
  layout?: RocheHeroTeaserLayout;
  left_icon?: string;
  mirror_layout?: boolean;
  page: RocheHeroTeaserInternalPageLink;
  right_icon?: string;
  slotted_secondary_action?: any[];
  teaser_image_ratio: string;
  button_title?: string;
  slotted_hero_image?: RocheHeroTeaserImage;
};

type RocheHeroTeaserProps = BlokToComponentProps<BaseBlok & RocheHeroTeaserType>;

export const RocheHeroTeaser = ({
  updateHoistedState,
  getComponent,
  slot,
  blok,
}: RocheHeroTeaserProps): JSX.Element => {
  const [teaser, setTeaser] = useState<PageItem>(blok.page?.story?.preRenderContent || ({} as PageItem));
  const secondaryButtonRef = useRef<HTMLDivElement>(null);
  const teaserOverline = blok.layout === 'teaser-without-overline' ? undefined : teaser.content?.teaser_overline;
  const primaryActionUrl = StringService.sanitizeUrl(`/${teaser?.full_slug}`);

  const readMoreTranslation = useTranslation('read more');
  const primaryButtonText =
    blok.button_title !== '' && blok.button_title ? blok.button_title : StringService.capitalize(readMoreTranslation);

  const secondaryButton = blokToComponent({
    getComponent,
    blok: blok.slotted_secondary_action[0],
  });

  const imageOverwriteSource = useMemo(
    () => parseAssetSource(blok.slotted_hero_image[0]?.src?.filename) ?? blok.slotted_hero_image[0]?.brightcove_id,
    [blok.slotted_hero_image],
  );

  const teaserImageSource = useMemo(
    () =>
      parseAssetSource(teaser.content?.teaser_image?.[0]?.src?.filename) ??
      teaser.content?.teaser_image?.[0]?.brightcove_id,
    [teaser.content?.teaser_image],
  );

  const imageRatio = imageOverwriteSource
    ? (blok.slotted_hero_image[0].ratio as RocheUIImageRatio)
    : (blok.teaser_image_ratio as RocheUIImageRatio);

  const trackPrimaryButtonClick = () => {
    TrackingService.push({
      event: 'teaser_click',
      component_name: 'roche-hero-teaser',
      additionalParameters: {
        teaser_title: teaser.content?.teaser_title,
        teaser_url: primaryActionUrl,
      },
    });
  };

  useEffect(() => {
    if ((teaser && Object.keys(teaser).length > 0) || !blok.page?.story?.id) return;

    TeaserService.getTeaser(blok.page?.story.id).then((result) => {
      setTeaser(result);
      // eslint-disable-next-line no-underscore-dangle
      return updateHoistedState?.(1, 1, blok._uid, result);
    });
    // eslint-disable-next-line no-underscore-dangle
  }, [blok.page?.story?.id, updateHoistedState, blok._uid]);

  // remove this when button component is moved to roche ui lib
  useEffect(() => {
    const secondaryButton = secondaryButtonRef.current?.querySelector('roche-button') as unknown as HTMLElement;
    if (secondaryButton) {
      secondaryButton.style.margin = '0px';
      secondaryButton.style.padding = '0px';
    }
  }, [secondaryButtonRef.current]);

  if (!teaser.content) return null;

  return (
    <div slot={slot}>
      <RocheUIHeroTeaser
        teaserTitle={teaser.content?.teaser_title}
        teaserOverline={teaserOverline}
        teaserDescription={teaser.content?.teaser_description}
        teaserImage={imageOverwriteSource ?? teaserImageSource}
        teaserImageRatio={imageRatio}
        primaryButtonText={primaryButtonText}
        primaryButtonUrl={primaryActionUrl}
        onPrimaryButtonClick={trackPrimaryButtonClick}
        secondaryButton={secondaryButton && <div ref={secondaryButtonRef}>{secondaryButton}</div>}
        leftIcon={blok.left_icon}
        rightIcon={blok.right_icon}
        imageOnLeftSide={blok.mirror_layout}
      />
    </div>
  );
};
